import { countryCodeToFlag } from "@/lib/languageFlag";

// Drapeau du pays detecte pour un plugin connecte.
//
// La localisation vient de ip-api.com (back/.../plugin_status/services.py) :
// - `country_code` (ISO 3166 alpha-2) est le champ fiable, ajoute juin 2026.
// - `country` est le nom anglais ("France", "United States", ...). Conserve en
//   fallback pour les entrees Redis encore en cache SANS `country_code`
//   (TTL geo 24h — elles n'ont pas encore ete re-resolues).
// Les IP privees renvoient {city:"Local", country:"Local"} : pas de drapeau.

// Noms anglais ip-api.com -> ISO 3166 alpha-2. Liste des pays les plus frequents
// cote RecrutAuto + fallback : un pays absent affiche juste son nom sans drapeau.
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  France: "FR",
  "United States": "US",
  "United Kingdom": "GB",
  Germany: "DE",
  Spain: "ES",
  Italy: "IT",
  Portugal: "PT",
  Netherlands: "NL",
  Belgium: "BE",
  Luxembourg: "LU",
  Switzerland: "CH",
  Austria: "AT",
  Ireland: "IE",
  Denmark: "DK",
  Sweden: "SE",
  Norway: "NO",
  Finland: "FI",
  Iceland: "IS",
  Poland: "PL",
  "Czechia": "CZ",
  "Czech Republic": "CZ",
  Slovakia: "SK",
  Hungary: "HU",
  Romania: "RO",
  Bulgaria: "BG",
  Greece: "GR",
  Croatia: "HR",
  Slovenia: "SI",
  Serbia: "RS",
  Ukraine: "UA",
  Russia: "RU",
  Turkey: "TR",
  Lithuania: "LT",
  Latvia: "LV",
  Estonia: "EE",
  Canada: "CA",
  Mexico: "MX",
  Brazil: "BR",
  Argentina: "AR",
  Chile: "CL",
  Colombia: "CO",
  Morocco: "MA",
  Algeria: "DZ",
  Tunisia: "TN",
  Egypt: "EG",
  "South Africa": "ZA",
  Nigeria: "NG",
  Senegal: "SN",
  "Ivory Coast": "CI",
  "Côte d'Ivoire": "CI",
  Cameroon: "CM",
  Israel: "IL",
  "United Arab Emirates": "AE",
  "Saudi Arabia": "SA",
  Qatar: "QA",
  India: "IN",
  Pakistan: "PK",
  China: "CN",
  "Hong Kong": "HK",
  Japan: "JP",
  "South Korea": "KR",
  Singapore: "SG",
  Malaysia: "MY",
  Indonesia: "ID",
  Thailand: "TH",
  Vietnam: "VN",
  Philippines: "PH",
  Australia: "AU",
  "New Zealand": "NZ",
};

interface PluginLocation {
  country_code?: string | null;
  country?: string | null;
}

/**
 * Code pays ISO 3166 alpha-2 (MAJUSCULE) d'un plugin, ou null si inconnu /
 * IP privee ("Local"). Prefere `country_code` (fiable, ip-api.com), retombe sur
 * le mapping nom anglais -> code. Sert au drapeau IMAGE (flag-icons, cf.
 * `components/CountryFlag.tsx`) ET a l'emoji historique (`locationFlag`).
 */
export function locationCountryCode(
  location: PluginLocation | null | undefined,
): string | null {
  if (!location) return null;
  if (location.country_code) return location.country_code.toUpperCase();
  if (location.country) return COUNTRY_NAME_TO_CODE[location.country] ?? null;
  return null;
}

/**
 * Emoji drapeau du pays d'un plugin. Renvoie "" si inconnu / IP privee.
 *
 * ⚠️ Les emojis-drapeaux ne s'affichent PAS sur Chrome/Edge sous Windows : ils
 * retombent sur les 2 lettres ("FR"). Pour l'UI, preferer `<CountryFlag>` qui
 * rend une vraie image SVG (flag-icons). Cette fonction reste pour les usages
 * non-DOM / tests.
 */
export function locationFlag(location: PluginLocation | null | undefined): string {
  const code = locationCountryCode(location);
  return code ? countryCodeToFlag(code) : "";
}
